import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiVentas } from '../../services/Axios';
import Loader from '../utils/loader/Loader';
import { 
  FaArrowUp, 
  FaArrowDown, 
  FaChartLine, 
  FaMoneyBillWave,
  FaShoppingCart,
  FaWallet,
  FaChartPie,
  FaBoxes,
  FaCalendarAlt,
} from 'react-icons/fa';
import CustomSelect from '../utils/implements/CustomSelect';
import ReactECharts from 'echarts-for-react';

interface DailySale {
  _id: string;
  total: number;
}

interface DailyData {
  _id: string;
  total: number;
}

interface PaymentMethod {
  _id: string;
  count: number;
}

interface TopProduct {
  productId: string;
  productName: string;
  totalQuantity: number;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const InventorySalesDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dailySalesData, setDailySalesData] = useState<DailySale[]>([]);
  const [dailyExpensesData, setDailyExpensesData] = useState<DailyData[]>([]);
  const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethod[]>([]);
  const [topProductsData, setTopProductsData] = useState<TopProduct[]>([]);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [totalSalesIssued, setTotalSalesIssued] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [profitMargin, setProfitMargin] = useState(0);
  const [period, setPeriod] = useState<string>('today');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      const response = await apiVentas.get('/api/sales/dashboard-metrics', { params });
      const { dailySales, dailyExpenses, paymentMethods, topProducts, totals, totalSalesIssued, totalExpenses } = response.data;

      setDailySalesData(dailySales || []);
      setDailyExpensesData(dailyExpenses || []);
      setPaymentMethodData(paymentMethods || []);
      setTopProductsData(topProducts || []);
      setTotalSalesAmount(totals?.totalSalesAmount || 0);
      setTotalSalesIssued(totalSalesIssued || 0);
      setTotalExpenses(totalExpenses || 0);
      setProfitMargin((totals?.totalSalesAmount || 0) - totalExpenses);
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allDates = useMemo(() => {
    return Array.from(new Set([
      ...dailySalesData.map(item => item._id),
      ...dailyExpensesData.map(item => item._id)
    ])).sort();
  }, [dailySalesData, dailyExpensesData]);

  const periodOptions = [
    { label: "Hoy", value: "today" },
    { label: "Últimos 2 Días", value: "last_2_days" },
    { label: "Últimos 7 Días", value: "last_7_days" },
    { label: "Último Mes", value: "last_1_month" },
  ];

  const lineChartOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151', fontSize: 13 },
      padding: [10, 15],
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);'
    },
    legend: {
      data: ['Ventas', 'Gastos'],
      textStyle: { color: '#4B5563', fontSize: 14, fontWeight: 500 },
      top: 10,
      itemGap: 20
    },
    grid: { 
      left: '3%', 
      right: '4%', 
      bottom: '8%', 
      top: '15%',
      containLabel: true 
    },
    xAxis: {
      type: 'category',
      data: allDates,
      axisLine: { lineStyle: { color: '#E5E7EB' } },
      axisLabel: { 
        color: '#6B7280',
        fontSize: 12,
        rotate: allDates.length > 10 ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#E5E7EB' } },
      axisLabel: { 
        color: '#6B7280',
        fontSize: 12,
        formatter: (value: number) => `$${(value / 1000).toFixed(0)}k`
      },
      splitLine: { lineStyle: { color: '#F3F4F6', type: 'dashed' } }
    },
    series: [
      {
        name: 'Ventas',
        type: 'line',
        smooth: true,
        data: allDates.map(date => dailySalesData.find(i => i._id === date)?.total || 0),
        lineStyle: {
          width: 3,
          color: '#10B981',
          shadowColor: 'rgba(16, 185, 129, 0.3)',
          shadowBlur: 10
        },
        itemStyle: {
          color: '#10B981',
          borderWidth: 2,
          borderColor: '#fff'
        },
        areaStyle: { 
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.05)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 8
      },
      {
        name: 'Gastos',
        type: 'line',
        smooth: true,
        data: allDates.map(date => dailyExpensesData.find(i => i._id === date)?.total || 0),
        lineStyle: {
          width: 3,
          color: '#EF4444',
          shadowColor: 'rgba(239, 68, 68, 0.3)',
          shadowBlur: 10
        },
        itemStyle: {
          color: '#EF4444',
          borderWidth: 2,
          borderColor: '#fff'
        },
        areaStyle: { 
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.05)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 8
      }
    ]
  }), [allDates, dailySalesData, dailyExpensesData]);

  const barChartOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151' },
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);'
    },
    grid: { 
      left: '3%', 
      right: '4%', 
      bottom: '10%', 
      top: '5%',
      containLabel: true 
    },
    xAxis: {
      type: 'category',
      data: topProductsData.map(item => item.productName),
      axisLabel: { 
        color: '#6B7280',
        fontSize: 11,
        interval: 0,
        rotate: 30
      },
      axisLine: { lineStyle: { color: '#E5E7EB' } }
    },
    yAxis: { 
      type: 'value',
      axisLabel: { 
        color: '#6B7280',
        fontSize: 12
      },
      splitLine: { lineStyle: { color: '#F3F4F6', type: 'dashed' } }
    },
    series: [
      {
        data: topProductsData.map((item, index) => ({
          value: item.totalQuantity,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: COLORS[index % COLORS.length] },
                { offset: 1, color: COLORS[index % COLORS.length] + '80' }
              ]
            },
            borderRadius: [8, 8, 0, 0],
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowBlur: 5
          }
        })),
        type: 'bar',
        barWidth: '60%',
        label: {
          show: true,
          position: 'top',
          color: '#374151',
          fontWeight: 'bold',
          fontSize: 11
        }
      }
    ]
  }), [topProductsData]);

  const pieChartOptions = useMemo(() => ({
    tooltip: { 
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151' },
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: { 
      top: 'bottom',
      textStyle: { color: '#4B5563', fontSize: 13 },
      itemGap: 15
    },
    series: [
      {
        name: 'Métodos de Pago',
        type: 'pie',
        radius: ['45%', '75%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 3,
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.1)'
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}\n{d}%',
          fontSize: 12,
          fontWeight: 'bold',
          color: '#374151'
        },
        labelLine: { 
          show: true,
          length: 15,
          length2: 10
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 15,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          },
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold'
          }
        },
        data: paymentMethodData.map((item, index) => ({
          value: item.count,
          name: item._id.charAt(0).toUpperCase() + item._id.slice(1),
          itemStyle: { color: COLORS[index % COLORS.length] }
        }))
      }
    ]
  }), [paymentMethodData]);

  const MetricCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    gradient,
    trend 
  }: { 
    title: string;
    value: React.ReactNode;
    subtitle: string;
    icon: React.ComponentType<{ className?: string }>;
    gradient: string;
    trend?: 'up' | 'down';
  }) => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div className={`h-2 ${gradient}`}></div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg ${gradient} bg-opacity-10`}>
            <Icon className="text-2xl text-white" />
          </div>
          {trend && (
            <div className={`flex items-center ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <FaArrowUp className="text-sm" /> : <FaArrowDown className="text-sm" />}
            </div>
          )}
        </div>
        <h4 className="text-sm font-medium text-gray-600 mb-2">{title}</h4>
        <p className="text-2xl font-bold text-gray-900 mb-2">{value}</p>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <FaChartLine className="text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Dashboard Empresarial</h1>
              <p className="text-blue-100 mt-1">Análisis completo de ventas, gastos y rendimiento</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <FaCalendarAlt className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-700">Período de Análisis</h3>
          </div>
          <CustomSelect options={periodOptions} value={period} onChange={setPeriod} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div className="flex flex-col gap-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total de Ventas"
              value={`$${totalSalesAmount.toLocaleString('es-CO')}`}
              subtitle="Ingresos totales del período"
              icon={FaMoneyBillWave}
              gradient="bg-gradient-to-r from-green-500 to-emerald-600"
              trend="up"
            />
            
            <MetricCard
              title="Total de Gastos"
              value={`$${totalExpenses.toLocaleString('es-CO')}`}
              subtitle="Egresos totales del período"
              icon={FaWallet}
              gradient="bg-gradient-to-r from-red-500 to-pink-600"
              trend="down"
            />
            
            <MetricCard
              title="Margen de Ganancia"
              value={
                <span className={profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                  ${Math.abs(profitMargin).toLocaleString('es-CO')}
                </span>
              }
              subtitle={profitMargin >= 0 ? 'Utilidad neta' : 'Pérdida neta'}
              icon={FaChartLine}
              gradient="bg-gradient-to-r from-blue-500 to-indigo-600"
              trend={profitMargin >= 0 ? 'up' : 'down'}
            />
            
            <MetricCard
              title="Ventas Realizadas"
              value={totalSalesIssued.toString()}
              subtitle="Transacciones completadas"
              icon={FaShoppingCart}
              gradient="bg-gradient-to-r from-purple-500 to-violet-600"
            />
          </div>

          {/* Line Chart - Sales vs Expenses */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FaChartLine className="text-blue-600 text-xl" />
                <h4 className="text-lg font-bold text-gray-800">Análisis de Ventas vs. Gastos</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">Comparativa de ingresos y egresos en el tiempo</p>
            </div>
            <div className="p-6">
              <ReactECharts option={lineChartOptions} style={{ height: '400px', width: '100%' }} />
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart - Top Products */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <FaBoxes className="text-purple-600 text-xl" />
                  <h4 className="text-lg font-bold text-gray-800">Productos Más Vendidos</h4>
                </div>
                <p className="text-sm text-gray-600 mt-1">Top 5 productos por cantidad vendida</p>
              </div>
              <div className="p-6">
                {topProductsData.length > 0 ? (
                  <ReactECharts option={barChartOptions} style={{ height: '350px', width: '100%' }} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-80 text-gray-400">
                    <FaBoxes className="text-5xl mb-3" />
                    <p className="text-lg">No hay datos de productos</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pie Chart - Payment Methods */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <FaChartPie className="text-green-600 text-xl" />
                  <h4 className="text-lg font-bold text-gray-800">Métodos de Pago</h4>
                </div>
                <p className="text-sm text-gray-600 mt-1">Distribución de transacciones por método</p>
              </div>
              <div className="p-6">
                {paymentMethodData.length > 0 ? (
                  <ReactECharts option={pieChartOptions} style={{ height: '350px', width: '100%' }} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-80 text-gray-400">
                    <FaChartPie className="text-5xl mb-3" />
                    <p className="text-lg">No hay datos de métodos de pago</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Footer */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <FaChartLine className="text-blue-600 text-xl" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">Resumen del Período</h4>
                <p className="text-sm text-gray-600">
                  Durante este período se registraron <strong>{totalSalesIssued} ventas</strong> con un ingreso total de{' '}
                  <strong className="text-green-600">${totalSalesAmount.toLocaleString('es-CO')}</strong> y gastos por{' '}
                  <strong className="text-red-600">${totalExpenses.toLocaleString('es-CO')}</strong>, resultando en un{' '}
                  {profitMargin >= 0 ? (
                    <strong className="text-green-600">margen positivo de ${profitMargin.toLocaleString('es-CO')}</strong>
                  ) : (
                    <strong className="text-red-600">déficit de ${Math.abs(profitMargin).toLocaleString('es-CO')}</strong>
                  )}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySalesDashboard;